import Box from "@mui/material/Box";
import {
  CircularProgress,
  debounce,
  Paper,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../common/store/store";
import { getUserInputForStepId } from "../../../components/wizard/util";
import {
  Alternatives,
  Parameter,
  ParameterType,
  ParameterValue,
} from "../../../common/types/parameters";
import {
  calculateOutput,
  wasCalculationAborted,
} from "../../../api/module-api";
import {
  getParameterStates,
  setUserInput as setModuleUserInput,
  resetUserInputsWithId as resetModuleUserInputsWithId,
} from "../../../common/store/module-parameters-slice";
import {
  getDisplayValue,
  getType,
  parseAsSelectionAlternative,
} from "../../../components/data-presentation/util";
import useAPIError from "../../../common/hooks/useAPIError";
import AngularKlimatModule from "./klimat-angular";
import ParameterSection from "../parameter-section";
import theme from "../../../components/theme/theme";
import { makeStyles, createStyles } from "@mui/styles";
import { baseApiUrl } from "../../../base";
import useWizard from "../../../common/hooks/useWizard";
import useHeatingTechnologySelection from "../../../common/hooks/useHeatingTechnologySelection";
import HideableChip from "../../../components/navigation/hideable-chip";
import { getLabel } from "../../../api/label-api";
import { isCalculated } from "../../../common/util/heating-technology-visibility";
import { getAsAlternative } from "../../../components/parameters/util";

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: "15px",
    },
  })
);

const calcModuleId = "klimat";

export default function Klimat(params: {
  forceParameterReload?: boolean;
  onReload?: () => void;
  sx?: SxProps;
}) {
  const classes = useStyles(theme);

  const { customer, economicProfile } = useParams();
  const { addError, deleteError } = useAPIError();
  const {
    steps: wizardSteps,
    updateParameterValue: updateWizardParameterValue,
    isWizardParameter,
    wizardIsCompleted,
  } = useWizard(customer!, economicProfile!);

  let moduleParameterInputs = useAppSelector((state) =>
    getParameterStates(state, {
      customer: customer,
      economicProfile: economicProfile,
    })
  );
  const [outputParameters, setOutputParameters] = useState<Parameter[]>([]);

  const dispatch = useAppDispatch();

  function onValidParameterChange(id: string, value: string) {
    if (isWizardParameter(id)) {
      let parameter = outputParameters.find((parameter) => parameter.id === id);

      if (parameter !== null && parameter !== undefined) {
        saveWizardParameter(
          id,
          value,
          getDisplayValue(
            value,
            false,
            getType(parameter),
            parameter?.stylingConfig,
            true
          ) ?? value
        );
      }
    } else {
      saveModuleParameter(id, value);
    }
  }

  function saveWizardParameter(
    id: string,
    value: string,
    formattedValue: string
  ) {
    updateWizardParameterValue({ id, value }, formattedValue);
  }

  function saveModuleParameter(id: string, value: string) {
    dispatch(
      setModuleUserInput({
        id: id,
        value: value,
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  const calculateOutputDelayed = useCallback(
    debounce(
      (inputs: ParameterValue[], callback: (output: Parameter[]) => void) => {
        calculateOutput(calcModuleId, inputs, customer, economicProfile)
          .then((calculationResult) => {
            if (wasCalculationAborted(calculationResult)) {
              return;
            }

            callback(calculationResult);
          })
          .catch(() => addError("Error"));
      },
      600
    ),
    [customer, economicProfile]
  );

  useEffect(() => {
    if (wizardIsCompleted) {
      let inputs = wizardSteps
        .flatMap((step) => step.inputParameters)
        .map(
          (parameter) =>
            ({ id: parameter.id, value: parameter.value }) as ParameterValue
        )
        .concat(moduleParameterInputs);
      calculateOutputDelayed(inputs, (parameters) => {
        deleteError();
        setOutputParameters(parameters);
      });
    }
  }, [
    moduleParameterInputs,
    customer,
    economicProfile,
    addError,
    deleteError,
    wizardIsCompleted,
    wizardSteps,
    calculateOutputDelayed,
  ]);

  useEffect(() => {
    if (params.forceParameterReload) {
      // Check if parameters should be reloaded
      let paramsToReset = outputParameters
        .filter((parameter) =>
          isSelectionParameterWithUnmodifiableAlternatives(parameter)
        )
        .map((parameter) => parameter.id);

      if (params.onReload !== undefined && params.onReload !== null) {
        params.onReload();
      }

      dispatch(
        resetModuleUserInputsWithId({
          ids: paramsToReset,
          profile: { customer: customer, economicProfile: economicProfile },
        })
      );
    }
  }, [customer, dispatch, economicProfile, outputParameters, params]);

  const [districtHeatingLabels, setDistrictHeatingLabels] =
    useState<Parameter>();
  const [groundSourceHeatPumpLabels, setGroundSourceHeatPumpLabels] =
    useState<Parameter>();
  const [airSourceHeatPumpLabels, setAirSourceHeatPumpLabels] =
    useState<Parameter>();
  const [exhaustAirHeatPumpLabels, setExhaustAirHeatPumpLabels] =
    useState<Parameter>();
  const [pelletLabels, setPelletLabels] = useState<Parameter>();

  const { isHidden, switchVisibilitySetting } = useHeatingTechnologySelection();

  const district = parseAsSelectionAlternative(
    getUserInputForStepId("DistrictSelected", wizardSteps)?.at(0)?.value
  )?.value;
  const building = parseAsSelectionAlternative(
    getUserInputForStepId("BuildingProfile", wizardSteps)?.at(0)?.value
  )?.value;

  useEffect(() => {
    if (district !== undefined && building !== undefined) {
      getLabel(
        "DistrictHeatingTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setDistrictHeatingLabels(response);
      });
      getLabel(
        "GroundSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setGroundSourceHeatPumpLabels(response);
      });
      getLabel(
        "AirSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setAirSourceHeatPumpLabels(response);
      });
      getLabel(
        "ExhaustAirHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setExhaustAirHeatPumpLabels(response);
      });
      getLabel("PelletTab", customer, district, building, economicProfile).then(
        (response) => {
          setPelletLabels(response);
        }
      );
    }
  }, [customer, district, building, economicProfile]);

  const heatingTechnologies = [
    {
      id: "DistrictHeating",
      title: districtHeatingLabels?.displayName || "Fjärrvärme",
    },
    {
      id: "GroundSource",
      title: groundSourceHeatPumpLabels?.displayName || "Bergvärme",
    },
    {
      id: "AirSource",
      title: airSourceHeatPumpLabels?.displayName || "Luft/vatten-värmepump",
    },
    {
      id: "ExhaustAir",
      title: exhaustAirHeatPumpLabels?.displayName || "Frånluftsvärmepump",
    },
    {
      id: "Pellet",
      title: pelletLabels?.displayName || "Pellets",
    },
  ].filter((tech) => isCalculated(tech, outputParameters));

  return outputParameters.length > 0 ? (
    <>
      <Typography variant="h1">Klimatmodulen</Typography>
      <Paper className={classes.card} elevation={3}>
        <Typography paragraph>
          Här beräknas utsläppen av klimatgaser för olika uppvärmningsalternativ
          för fastigheten. Värmebehovet kan justeras nedan.
        </Typography>
        <Typography paragraph>
          Beräkningen tar hänsyn till samtliga utsläppskonsekvenser som uppstår
          i energisystemet baserat på hur energisystemet ser ut under
          uppvärmningsalternativens livslängd, dvs de kommande 20 åren. Läs mer
          under "Om beräkningen".
        </Typography>
        <Typography paragraph>
          Klicka på staplarna för respektive uppvärmningsalternativ för att se
          mer detaljer kring hur utsläppen beräknas. I diagrammet längst ner går
          det också att klicka på staplarna för att läsa mer om olika bränslen
          som används i värmeproduktionen.
        </Typography>
      </Paper>
      <ParameterSection
        parameters={outputParameters}
        columns={[
          { sections: [{ title: "", parameters: ["AnnualHeatingDemand"] }] },
        ]}
        includeVat={false} // Not needed for non-currency parameters
        onValidParameterChange={onValidParameterChange}
        sx={{ minHeight: null, mt: 4, textAlign: "center" }}
      />
      <Box sx={{ pb: 7, backgroundColor: "white", ...params.sx }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent={"center"}
          paddingTop={4}
          marginBottom={2}
        >
          {heatingTechnologies.map((heatingTechnology) => (
            <HideableChip
              value={heatingTechnology.id}
              label={heatingTechnology.title}
              visible={!isHidden(heatingTechnology.id)}
              onSwitchVisibility={switchVisibilitySetting}
            />
          ))}
        </Stack>
        <AngularKlimatModule
          customer={customer}
          showDistrictHeating={!isHidden("DistrictHeating")}
          annualHeatingDemand={
            outputParameters.find((param) => param.id === "AnnualHeatingDemand")
              ?.value
          }
          pelletBoilerEfficiency={
            !isHidden("Pellet")
              ? outputParameters.find(
                  (param) => param.id === "PelletBoilerEfficiency"
                )?.value
              : undefined
          }
          airSourceHeatPumpEfficiency={
            !isHidden("AirSource")
              ? outputParameters.find(
                  (param) => param.id === "AirSourceHeatPumpEfficiency"
                )?.value
              : undefined
          }
          airSourceHeatPumpEnergyCoverage={
            !isHidden("AirSource")
              ? outputParameters.find(
                  (param) => param.id === "AirSourceHeatPumpEnergyCoverage"
                )?.value
              : undefined
          }
          exhaustAirHeatPumpEfficiency={
            !isHidden("ExhaustAir")
              ? outputParameters.find(
                  (param) => param.id === "ExhaustAirHeatPumpEfficiency"
                )?.value
              : undefined
          }
          exhaustAirHeatPumpEnergyCoverage={
            !isHidden("ExhaustAir")
              ? outputParameters.find(
                  (param) => param.id === "ExhaustAirHeatPumpEnergyCoverage"
                )?.value
              : undefined
          }
          groundSourceHeatPumpEfficiency={
            !isHidden("GroundSource")
              ? outputParameters.find(
                  (param) => param.id === "GroundSourceHeatPumpEfficiency"
                )?.value
              : undefined
          }
          groundSourceHeatPumpEnergyCoverage={
            !isHidden("GroundSource")
              ? outputParameters.find(
                  (param) => param.id === "GroundSourceHeatPumpEnergyCoverage"
                )?.value
              : undefined
          }
          supplementalHeatingSource={
            getAsAlternative(
              outputParameters.find(
                (param) => param.id === "SupplementalHeatingSource"
              )?.value
            )?.value
          }
          district={district}
          building={building}
          economicProfile={economicProfile}
          api={baseApiUrl}
        />
      </Box>
    </>
  ) : (
    <Box sx={{ textAlign: "center", ...params.sx }}>
      <CircularProgress />
    </Box>
  );
}

function isSelectionParameterWithUnmodifiableAlternatives(
  parameter: Parameter
): boolean {
  return (
    getType(parameter) === ParameterType.Selection &&
    (parameter.constraints as Alternatives) &&
    (parameter.constraints as Alternatives)
      .flatMap((alternativeList) => alternativeList)
      .some((alternative) => !alternative.editable)
  );
}
